import React, { useState, useEffect } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  LogOut,
  Loader2,
  Edit,
  Trash2,
  UserPlus,
  ChevronDown,
  Shield,
  ShieldCheck,
  Menu,
  X,
  Music,
  Clock,
  Calendar,
  FileText,
  TrendingUp,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StatCard from "./ui/StatCard";
import MiniChart from "./ui/MiniChart";
import ActivityChart from "./ui/ActivityChart";
import { toast } from "sonner";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import ReportStatusChart from "./ui/ReportStatusChart";
import CircleChart from "./ui/CircleChart";

// ProfileAvatar component
const ProfileAvatar = ({ name, role }) => {
  const bgColors = {
    admin: "bg-blue-600",
    "super user": "bg-emerald-600",
  };

  const bgColor = bgColors[role] || "bg-zinc-800";
  const firstLetter = name ? name.charAt(0).toUpperCase() : "?";

  return (
    <div
      className={`${bgColor} w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-medium text-sm sm:text-lg shadow-md flex-shrink-0`}
    >
      {firstLetter}
    </div>
  );
};

// StatusBadge component
const StatusBadge = ({ status, onClick, disabled, role }) => {
  const { t } = useTranslation();
  const statusKey =
    status === t("adminSettings.admins.status.active") ? "active" : "inactive";

  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium py-1 px-2 sm:px-3 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 whitespace-nowrap">
        <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0"></span>
        <span className="inline text-xs">
          {t(`adminSettings.admins.status.${statusKey}`)}
        </span>
      </span>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 text-xs font-medium py-1 px-2 sm:px-3 rounded-full transition-all duration-300 whitespace-nowrap ${
        statusKey === "active"
          ? "bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100"
          : "bg-red-50 text-red-500 border border-red-100 hover:bg-red-100"
      }`}
    >
      <span
        className={`w-2 h-2 rounded-full flex-shrink-0 ${
          statusKey === "active" ? "bg-emerald-500" : "bg-red-500"
        }`}
      ></span>
      <span className="inline text-xs">
        {t(`adminSettings.admins.status.${statusKey}`)}
      </span>
    </button>
  );
};

// RoleBadge component
const RoleBadge = ({ role }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 sm:gap-2">
      {role === "admin" ? (
        <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600 flex-shrink-0" />
      ) : (
        <ShieldCheck className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600 flex-shrink-0" />
      )}
      <span className="text-gray-700 font-medium capitalize text-xs sm:text-sm">
        {t(`adminSettings.admins.role.${role.replace(" ", "-")}`)}
      </span>
    </div>
  );
};

// Mobile Report Card Component
const MobileReportCard = ({
  report,
  onEdit,
  formatReportDate,
  formatLanguage,
  formatStatus,
  getStatusColorClass,
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 shadow-md">
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 text-sm truncate">
            {report.songTitle}
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            {formatLanguage(report.language)} •{" "}
            {formatReportDate(report.createdAt)}
          </p>
        </div>
        <span
          className={`inline-block px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${getStatusColorClass(
            report.status
          )}`}
        >
          {formatStatus(report.status)}
        </span>
      </div>
      <button
        onClick={() => onEdit(report)}
        className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-zinc-900 transition-all duration-300"
      >
        {t("adminSettings.reports.edit")}
      </button>
    </div>
  );
};

// Mobile Admin Card Component
const MobileAdminCard = ({
  admin,
  isSelected,
  onToggleSelection,
  onToggleStatus,
  onEdit,
  onDelete,
  statusUpdating,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg p-4 space-y-4 shadow-md ${
        isSelected ? "ring-2 ring-blue-500 ring-opacity-50" : ""
      }`}
    >
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          className="w-4 h-4 mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
          onChange={() => onToggleSelection(admin.id)}
          checked={isSelected}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3">
            <ProfileAvatar name={admin.name} role={admin.role} />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm truncate">
                {admin.name}
              </h4>
              <p className="text-xs text-gray-500 truncate">
                {admin.displayEmail}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-col space-y-2">
          <RoleBadge role={admin.role} />
          <StatusBadge
            status={admin.status}
            onClick={() => onToggleStatus(admin)}
            disabled={statusUpdating}
            role={admin.role}
          />
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(admin)}
            className="px-3 py-1.5 bg-zinc-800 text-white rounded-lg text-xs hover:bg-zinc-900 transition-colors"
          >
            {t("adminSettings.admins.edit")}
          </button>
          <button
            onClick={() => onDelete(admin.id)}
            disabled={admin.role === "admin"}
            className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
              admin.role === "admin"
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-white bg-red-600 hover:bg-red-700"
            }`}
          >
            {t("adminSettings.admins.delete")}
          </button>
        </div>
      </div>

      <div className="text-xs text-gray-500">{admin.employment}</div>
    </div>
  );
};

// Enhanced Professional ReportCard component
const ReportCard = ({ report, onEdit, formatReportDate, formatLanguage, formatStatus, getStatusColorClass }) => {
  const { t } = useTranslation();
  const status = report.status.toLowerCase();
  let statusIcon, statusAnim;
  if (status === "pending") {
    statusIcon = <Clock className="w-4 h-4 text-yellow-500 animate-pulse" />;
    statusAnim = "animate-pulse";
  } else if (status === "resolved") {
    statusIcon = <ShieldCheck className="w-4 h-4 text-green-500" />;
    statusAnim = "";
  } else {
    statusIcon = <X className="w-4 h-4 text-red-500" />;
    statusAnim = "";
  }
  // Song thumbnail: use gradient if no image
  const songThumb = (
    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-orange-200 via-orange-100 to-yellow-100 flex items-center justify-center shadow-md">
      <Music className="w-6 h-6 text-orange-500" />
    </div>
  );
  return (
    <div className="backdrop-blur-md bg-white/80 border border-gray-100 rounded-3xl shadow-md p-6 flex flex-col gap-4 hover:-translate-y-1 transition-all duration-200 group relative">
      {/* Floating Edit Button */}
      <button
        onClick={() => onEdit(report)}
        className="absolute top-3 right-3 bg-zinc-900 text-white rounded-full p-2 shadow-md hover:bg-orange-500 transition-colors duration-200 z-10"
        title={t("adminSettings.reports.edit")}
      >
        <Edit className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-4">
        {songThumb}
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 text-lg truncate tracking-tight">
            {report.songTitle}
          </h4>
          <div className="flex gap-2 mt-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700 gap-1 font-medium shadow-md">
              <FileText className="w-3 h-3 text-gray-400" />{formatLanguage(report.language)}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700 gap-1 font-medium shadow-md">
              <Calendar className="w-3 h-3 text-gray-400" />{formatReportDate(report.createdAt)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold shadow ${getStatusColorClass(report.status)} ${statusAnim} bg-opacity-80`}>{statusIcon}{formatStatus(report.status)}</span>
      </div>
    </div>
  );
};

const AdminSettings = () => {
  const [admins, setAdmins] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    password: "",
    role: "super user",
    isActive: true,
  });
  const [editAdmin, setEditAdmin] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [activityFilter, setActivityFilter] = useState("Month");
  const [userRole, setUserRole] = useState("");
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState(null);
  const [songCount, setSongCount] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [reportSearchTerm, setReportSearchTerm] = useState("");
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [editedSongsCount, setEditedSongsCount] = useState(0);
  const [editedReportsCount, setEditedReportsCount] = useState(0);
  const [knowledgeGain, setKnowledgeGain] = useState("0%");
  const [songsAddedThisMonth, setSongsAddedThisMonth] = useState(0);
  const [songs, setSongs] = useState([]);
  const [lastThreeMonths, setLastThreeMonths] = useState([]);
  const [reportPage, setReportPage] = useState(1);
  const reportsPerPage = 9;
  const [totalReportPages, setTotalReportPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);

  // Check authentication and role
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const role = localStorage.getItem("adminRole");
    if (!token || !["admin", "super user"].includes(role)) {
      navigate("/admin");
    } else {
      setUserRole(role);
      if (role === "admin") {
        fetchAdmins();
      }
      fetchReports(reportPage);
    }
    // eslint-disable-next-line
  }, [navigate]);

  // Fetch reports when page changes
  useEffect(() => {
    fetchReports(reportPage);
    // eslint-disable-next-line
  }, [reportPage]);

  // Fetch song count and last sync data
  useEffect(() => {
    const fetchSongCount = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongsCount`
        );
        if (!response.ok)
          throw new Error(`Song count error: ${response.status}`);
        const data = await response.json();
        setSongCount(data.count || 0);
      } catch (error) {
        console.error("Error fetching song count:", error);
        toast.error(t("admin.errorFetchingSongCount"));
        setSongCount(0);
      }
    };

    const fetchSongsAddedThisMonth = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sync/songs-added-this-month`
        );
        if (!response.ok)
          throw new Error(`Songs added error: ${response.status}`);
        const data = await response.json();
        setSongsAddedThisMonth(data.totalSongsAdded || 0);
      } catch (error) {
        console.error("Error fetching songs added this month:", error);
        toast.error(t("admin.errorFetchingSongsAdded"));
        setSongsAddedThisMonth(0);
      }
    };

    const fetchLastSync = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sync/last-sync`
        );
        if (!response.ok)
          throw new Error(`Last sync error: ${response.status}`);
        const data = await response.json();
        const syncData = data.data || data;
        if (Array.isArray(syncData) && syncData.length > 0) {
          const sortedSyncs = [...syncData].sort(
            (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
          );
          setLastSync(sortedSyncs[0]);
        } else {
          setLastSync(syncData);
        }
      } catch (error) {
        console.error("Error fetching last sync info:", error);
        toast.error(t("admin.errorFetchingLastSync"));
      }
    };

    const fetchEditedSongs = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/song-edits/count`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok)
          throw new Error(`Edited songs error: ${response.status}`);
        const data = await response.json();
        setEditedSongsCount(data.count || 0);
      } catch (error) {
        console.error("Error fetching edited songs count:", error);
        toast.error(t("admin.errorFetchingEditedSongs"));
        setEditedSongsCount(0);
      }
    };

    const fetchEditedReports = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/reports`
        );
        if (!response.ok)
          throw new Error(`Edited reports error: ${response.status}`);
        const data = await response.json();
        setEditedReportsCount(data.reports?.length || 0);
      } catch (error) {
        console.error("Error fetching edited reports count:", error);
        toast.error(t("admin.errorFetchingEditedReports"));
        setEditedReportsCount(0);
      }
    };

    const fetchSongs = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongs`
        );
        if (!response.ok)
          throw new Error(`Songs fetch error: ${response.status}`);
        const data = await response.json();
        if (data.songs && Array.isArray(data.songs)) {
          setSongs(data.songs);
          setTotalPages(data.pagination?.pages || 1);
        } else {
          setSongs([]);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching songs:", error);
        toast.error(t("admin.errorFetchingData"));
        setSongs([]);
        setTotalPages(1);
      }
    };

    setIsLoading(true);
    Promise.all([
      fetchSongCount(),
      fetchSongsAddedThisMonth(),
      fetchLastSync(),
      fetchEditedSongs(),
      fetchEditedReports(),
      fetchSongs(),
    ]).finally(() => setIsLoading(false));
  }, [t]);

  // Calculate knowledge gain
  useEffect(() => {
    if (songCount > 0 && songsAddedThisMonth >= 0) {
      const percentage = (songsAddedThisMonth / songCount) * 100;
      const formattedPercentage = new Intl.NumberFormat(i18n.language, {
        style: "percent",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(percentage / 100);
      setKnowledgeGain(`+${formattedPercentage}`);
    } else {
      setKnowledgeGain("+0%");
    }
  }, [songCount, songsAddedThisMonth, i18n.language]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t("admin.never");
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Calculate next scheduled sync time
  const getNextScheduledSync = () => {
    const now = new Date();
    const nextSync = new Date(now);
    nextSync.setDate(now.getDate() + ((7 - now.getDay()) % 7));
    nextSync.setHours(2, 0, 0, 0);

    if (now.getDay() === 0 && now.getHours() < 2) {
      nextSync.setDate(now.getDate());
    }

    if (nextSync <= now) {
      nextSync.setDate(nextSync.getDate() + 7);
    }

    return formatDate(nextSync);
  };

  // Fetch admins from backend
  const fetchAdmins = async (page = 1) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/users?page=${page}&limit=10`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const mappedAdmins = response.data.map((admin) => ({
        id: admin._id,
        name: admin.username,
        displayEmail: admin.username.toLowerCase() + "@praisesawa.com",
        email: admin.username.toLowerCase() + "@praisesawa.com",
        role: admin.role,
        employment:
          admin.role === "admin"
            ? t("adminSettings.admins.employment.fullAccess")
            : t("adminSettings.admins.employment.limitedAccess"),
        status:
          admin.isActive !== false
            ? t("adminSettings.admins.status.active")
            : t("adminSettings.admins.status.inactive"),
        isActive: admin.isActive !== false,
        note:
          admin.role === "admin"
            ? t("adminSettings.admins.note.systemAdmin")
            : t("adminSettings.admins.note.contentManager"),
      }));
      setAdmins(mappedAdmins);
      setTotalPages(Math.ceil(response.data.length / 10));
      setCurrentPage(page);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        setError(
          err.response?.data?.message || t("adminSettings.admins.error.fetchAdmins")
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch reports from backend
  const fetchReports = async (page = 1) => {
    setReportsLoading(true);
    setReportsError(null);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/reports`,
        {
          params: { page, limit: reportsPerPage },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const reportsData = response.data.reports;
      const pagination = response.data.pagination || {};
      const total = pagination.total || reportsData.length;
      const pages = pagination.pages || 1;
      const enrichedReports = await Promise.all(
        reportsData.map(async (report) => {
          let songTitle = report.songTitle || "Untitled Song";
          if (!report.songTitle) {
            try {
              const songResponse = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${report.songID}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              songTitle = songResponse.data.title || "Untitled Song";
            } catch (songError) {
              console.error(
                `Error fetching song title for songID ${report.songID}:`,
                songError
              );
            }
          }
          return { ...report, songTitle };
        })
      );
      setReports(enrichedReports);
      setTotalReports(total);
      setTotalReportPages(pages);
      setReportPage(page);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setReportsError(
        t("adminSettings.reports.error") ||
          "Failed to load reports. Please try again."
      );
    } finally {
      setReportsLoading(false);
    }
  };

  // Toggle admin selection
  const toggleAdminSelection = (id) => {
    setSelectedAdmins((prev) =>
      prev.includes(id)
        ? prev.filter((adminId) => adminId !== id)
        : [...prev, id]
    );
  };

  // Handle add admin
  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsAddModalOpen(false);
      setNewAdmin({
        username: "",
        password: "",
        role: "super user",
        isActive: true,
      });
      fetchAdmins(currentPage);
    } catch (err) {
      setError(
        err.response?.data?.message || t("adminSettings.admins.error.addAdmin")
      );
    }
  };

  // Handle edit admin
  const handleEditAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${editAdmin.id}`,
        {
          username: editAdmin.username,
          password: editAdmin.password || undefined,
          role: editAdmin.role,
          isActive: editAdmin.isActive,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setIsEditModalOpen(false);
      setEditAdmin(null);
      fetchAdmins(currentPage);
    } catch (err) {
      setError(
        err.response?.data?.message || t("adminSettings.admins.error.editAdmin")
      );
    }
  };

  // Toggle admin active status
  const toggleAdminStatus = async (admin) => {
    if (admin.role === "admin") return;

    setStatusUpdating(true);
    try {
      const token = localStorage.getItem("adminToken");
      const newIsActive = !admin.isActive;
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${admin.id}`,
        {
          isActive: newIsActive,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setAdmins(
        admins.map((a) =>
          a.id === admin.id
            ? {
                ...a,
                isActive: newIsActive,
                status: t(
                  `adminSettings.admins.status.${
                    newIsActive ? "active" : "inactive"
                  }`
                ),
              }
            : a
        )
      );
    } catch (err) {
      setError(
        err.response?.data?.message || t("adminSettings.admins.error.updateStatus")
      );
    } finally {
      setStatusUpdating(false);
    }
  };

  // Handle delete admin
  const handleDeleteAdmin = async (id) => {
    if (window.confirm(t("adminSettings.confirmDelete"))) {
      try {
        const token = localStorage.getItem("adminToken");
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchAdmins(currentPage);
      } catch (err) {
        setError(
          err.response?.data?.message || t("adminSettings.admins.error.deleteAdmin")
        );
      }
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/logout`);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRole");
      navigate("/admin");
      window.location.reload();
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Open edit modal
  const openEditModal = (admin) => {
    setEditAdmin({
      id: admin.id,
      username: admin.name,
      password: "",
      role: admin.role,
      isActive: admin.isActive,
    });
    setIsEditModalOpen(true);
  };

  // Handle edit report
  const handleEditReport = (report) => {
    const { _id, songID, partType, verseIndex, lineIndex } = report;
    const queryParams = new URLSearchParams({
      partType,
      lineIndex: lineIndex.toString(),
      reportId: _id,
      ...(partType === "verse" && { verseIndex: verseIndex.toString() }),
    }).toString();
    navigate(`/edit-song/${songID}?${queryParams}`);
  };

  // Format date for reports
  const formatReportDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Capitalize language
  const formatLanguage = (language) => {
    return language.charAt(0).toUpperCase() + language.slice(1);
  };

  // Format report status
  const formatStatus = (status) => {
    return t(`adminSettings.status.${status.toLowerCase()}`);
  };

  // Get status color class
  const getStatusColorClass = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "resolved":
        return "text-green-600 bg-green-100";
      case "dismissed":
        return "text-red-600 bg-red-100";
      default:
        return "text-stone-300 bg-gray-100";
    }
  };

  // Filter admins based on search term
  const filteredAdmins = admins.filter(
    (admin) =>
      admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter reports based on search term (client-side, on current page only)
  const filteredReports = reports.filter(
    (report) =>
      report.songTitle.toLowerCase().includes(reportSearchTerm.toLowerCase()) ||
      report.language.toLowerCase().includes(reportSearchTerm.toLowerCase()) ||
      report.status.toLowerCase().includes(reportSearchTerm.toLowerCase())
  );

  // Pagination for reports (backend-driven)
  const handleReportPrevPage = () => {
    if (reportPage > 1) setReportPage(reportPage - 1);
  };
  const handleReportNextPage = () => {
    if (reportPage < totalReportPages) setReportPage(reportPage + 1);
  };
  const handleShowMoreReports = () => {
    navigate('/all-reports');
  };

  // Pagination for admins
  const handlePreviousPage = () => {
    if (currentPage > 1) fetchAdmins(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) fetchAdmins(currentPage + 1);
  };

  return (
    <div className="flex h-full overflow-hidden bg-white">
      <div className="flex-1 overflow-auto">
        <main className="px-3 sm:px-6 py-4 sm:py-6">
          {/* Top dashboard section - replaced with MainDashboard layout */}
          <div className="flex flex-col gap-4 pb-5">
            {/* Top: StatCards grid and ReportStatusChart */}
            <div className="flex flex-col lg:flex-row gap-4" style={{ minHeight: 260 }}>
              {/* StatCards grid */}
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 grid-rows-2 gap-4 flex-1"
              >
                <StatCard
                  title={t("admin.totalSongs")}
                  value={songCount.toString()}
                  subtitle={t("admin.songs")}
                  icon={<Music className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.lastSync")}
                  value={lastSync ? formatDate(lastSync.timestamp) : t("admin.never")}
                  icon={<Clock className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.nextScheduledSync")}
                  value={getNextScheduledSync()}
                  icon={<Calendar className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.editedSongs")}
                  value={editedSongsCount.toString()}
                  subtitle={t("admin.songs")}
                  icon={<Edit className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.editedReports")}
                  value={editedReportsCount.toString()}
                  subtitle={t("admin.reports")}
                  icon={<FileText className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.persangeThisMonth")}
                  value={knowledgeGain}
                  chart={<MiniChart />}
                  icon={<TrendingUp className="w-7 h-7 text-orange-500" />}
                />
              </div>
              {/* ReportStatusChart spans both rows */}
              <div
                className="flex-1 flex flex-col"
                style={{ minWidth: 0, height: "100%" }}
              >
                <ReportStatusChart style={{ height: "100%" }} />
              </div>
            </div>
            {/* Bottom: ActivityChart and CircleChart */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div
                className="flex-1"
              >
                <ActivityChart
                  title={t("admin.activity")}
                  filter={activityFilter}
                  onFilterChange={setActivityFilter}
                />
              </div>
              <div
                className="flex-1 flex items-center justify-center"
                style={{ minWidth: 0 }}
              >
                <CircleChart lastThreeMonths={lastThreeMonths} />
              </div>
            </div>
          </div>

          <div className="max-w-8xl space-y-6">
            {/* Recent Edits Reports Card */}
            <div className="rounded-2xl border border-gray-100 shadow-sm bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-neutral-700 flex items-center gap-2">
                  {t("adminSettings.reports.title")}
                </h2>
                {filteredReports.length > 6 && (
                  <button
                    className="px-4 py-1 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors shadow"
                    onClick={handleShowMoreReports}
                  >
                    {t("adminSettings.reports.showMore", "Show All")}
                  </button>
                )}
              </div>
              {reportsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-h-[200px]">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-gradient-to-tr from-gray-100 via-gray-50 to-white border border-gray-100 rounded-2xl p-4 animate-pulse h-40 shadow" />
                  ))}
                </div>
              ) : reportsError ? (
                <div className="p-4 text-red-500 text-center w-full col-span-full">
                  {reportsError}
                </div>
              ) : filteredReports.length === 0 ? (
                <div className="py-8 text-center text-neutral-700 text-sm w-full col-span-full">
                  {t("adminSettings.reports.noReports")}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {filteredReports.slice(0, 6).map((report) => (
                      <ReportCard
                        key={report._id}
                        report={report}
                        onEdit={handleEditReport}
                        formatReportDate={formatReportDate}
                        formatLanguage={formatLanguage}
                        formatStatus={formatStatus}
                        getStatusColorClass={getStatusColorClass}
                      />
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4 sm:gap-0">
                    <div className="text-base text-gray-500 order-2 sm:order-1 font-medium">
                      {t("adminSettings.reports.showing", {
                        count: (reportPage - 1) * reportsPerPage + filteredReports.length,
                        total: totalReports,
                      })}
                    </div>
                    <div className="flex items-center gap-3 order-1 sm:order-2 bg-white/80 rounded-full shadow-sm px-4 py-2">
                      <button
                        className="p-2 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-orange-50 transition-colors disabled:opacity-50"
                        onClick={handleReportPrevPage}
                        disabled={reportPage === 1}
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-400" />
                      </button>
                      <span className="text-base text-gray-700 font-semibold">
                        {reportPage} / {totalReportPages}
                      </span>
                      <button
                        className="p-2 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-orange-50 transition-colors disabled:opacity-50"
                        onClick={handleReportNextPage}
                        disabled={reportPage === totalReportPages || totalReportPages === 0}
                      >
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                      <button
                        className="ml-4 px-4 py-2 rounded-xl bg-black text-white font-bold shadow-sm hover:bg-zinc-900 transition-colors"
                        onClick={handleShowMoreReports}
                      >
                        {t("adminSettings.reports.showMore")}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Admin Users Section - Only for admin role */}
            {userRole === "admin" && (
              <Card className="rounded-[19px] border-[0.32px] border-gray-100 shadow-[0px_0px_1px_0px_rgba(0,0,0,0.04),0px_2px_6px_0px_rgba(0,0,0,0.04),0px_16px_24px_0px_rgba(0,0,0,0.06)]">
                <CardHeader className="pb-2 px-4 sm:px-6">
                  <CardTitle className="text-lg sm:text-xl font-semibold text-neutral-700">
                    {t("adminSettings.admins.title")}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    {t("adminSettings.admins.subtitle")}
                  </p>
                </CardHeader>
                <CardContent className="px-4 sm:px-6">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4 lg:gap-0">
                    <div className="relative w-full lg:w-auto">
                      <input
                        type="text"
                        placeholder={t(
                          "adminSettings.admins.searchPlaceholder"
                        )}
                        className="pl-9 pr-4 py-2 w-full lg:w-80 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
                      <span className="flex items-center text-sm text-gray-500 whitespace-nowrap">
                        {selectedAdmins.length}{" "}
                        {t("adminSettings.admins.selected")}
                      </span>

                      {selectedAdmins.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          <button className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-all duration-200">
                            {t("adminSettings.admins.bulkEdit")}
                          </button>
                          <button className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100 transition-all duration-200">
                            {t("adminSettings.admins.deactivate")}
                          </button>
                        </div>
                      )}

                      <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="w-full sm:w-auto h-8 px-3 bg-[#F59E0B] text-white rounded-lg text-center text-sm font-normal tracking-wide outline outline-1 outline-gray-100 hover:bg-[#ffa825] transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        <UserPlus className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {t("adminSettings.admins.add")}
                        </span>
                        <span className="sm:hidden">Add</span>
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                      <p className="text-sm">{error}</p>
                    </div>
                  )}

                  {isLoading ? (
                    <div className="flex justify-center items-center min-h-[200px]">
                      <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table */}
                      <div className="hidden lg:block">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="font-normal text-zinc-400 text-sm">
                                <input
                                  type="checkbox"
                                  className="w-5 h-5 rounded-md border border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  onChange={() =>
                                    setSelectedAdmins(
                                      selectedAdmins.length ===
                                        filteredAdmins.length
                                        ? []
                                        : filteredAdmins.map(
                                            (admin) => admin.id
                                          )
                                    )
                                  }
                                  checked={
                                    selectedAdmins.length ===
                                      filteredAdmins.length &&
                                    filteredAdmins.length > 0
                                  }
                                />
                              </TableHead>
                              <TableHead className="font-normal text-zinc-400 text-sm">
                                {t("adminSettings.admins.table.user")}
                              </TableHead>
                              <TableHead className="font-normal text-zinc-400 text-sm">
                                {t("adminSettings.admins.table.role")}
                              </TableHead>
                              <TableHead className="font-normal text-zinc-400 text-sm">
                                {t("adminSettings.admins.table.status")}
                              </TableHead>
                              <TableHead className="font-normal text-zinc-400 text-sm text-right">
                                {t("adminSettings.admins.table.actions")}
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredAdmins.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={5}
                                  className="py-4 text-center text-neutral-700 text-sm"
                                >
                                  {t("adminSettings.admins.noResults")}
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredAdmins.map((admin) => (
                                <TableRow
                                  key={admin.id}
                                  className="border-t border-gray-100"
                                >
                                  <TableCell className="py-4">
                                    <input
                                      type="checkbox"
                                      className="w-5 h-5 rounded-md border border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      onChange={() =>
                                        toggleAdminSelection(admin.id)
                                      }
                                      checked={selectedAdmins.includes(
                                        admin.id
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell className="py-4">
                                    <div className="flex items-center gap-3">
                                      <ProfileAvatar
                                        name={admin.name}
                                        role={admin.role}
                                      />
                                      <div>
                                        <div className="font-medium text-neutral-700 text-sm">
                                          {admin.name}
                                        </div>
                                        <div className="text-stone-300 text-xs">
                                          {admin.displayEmail}
                                        </div>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="py-4">
                                    <div className="flex items-center gap-2">
                                      <RoleBadge role={admin.role} />
                                      <div className="text-xs text-stone-300">
                                        {admin.employment}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="py-4">
                                    <StatusBadge
                                      status={admin.status}
                                      onClick={() => toggleAdminStatus(admin)}
                                      disabled={statusUpdating}
                                      role={admin.role}
                                    />
                                  </TableCell>
                                  <TableCell className="py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                      <button
                                        onClick={() => openEditModal(admin)}
                                        className="w-20 h-6 bg-[#333] rounded-lg text-center text-white text-sm font-normal tracking-wide outline outline-1 outline-gray-100 hover:bg-black transition-colors"
                                      >
                                        {t("adminSettings.admins.edit")}
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleDeleteAdmin(admin.id)
                                        }
                                        disabled={admin.role === "admin"}
                                        className={`w-20 h-6 rounded-lg text-center text-sm font-normal tracking-wide outline outline-1 outline-gray-100 transition-colors ${
                                          admin.role === "admin"
                                            ? "text-gray-300 bg-gray-100 cursor-not-allowed"
                                            : "text-white bg-red-600 hover:bg-red-700"
                                        }`}
                                        title={t(
                                          admin.role === "admin"
                                            ? "adminSettings.admins.cannotDelete"
                                            : "adminSettings.admins.delete"
                                        )}
                                      >
                                        {t("adminSettings.admins.delete")}
                                      </button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="block lg:hidden space-y-4">
                        {filteredAdmins.length === 0 ? (
                          <div className="py-8 text-center text-neutral-700 text-sm">
                            {t("adminSettings.admins.noResults")}
                          </div>
                        ) : (
                          filteredAdmins.map((admin) => (
                            <MobileAdminCard
                              key={admin.id}
                              admin={admin}
                              isSelected={selectedAdmins.includes(admin.id)}
                              onToggleSelection={toggleAdminSelection}
                              onToggleStatus={toggleAdminStatus}
                              onEdit={openEditModal}
                              onDelete={handleDeleteAdmin}
                              statusUpdating={statusUpdating}
                            />
                          ))
                        )}
                      </div>
                    </>
                  )}

                  <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4 sm:gap-0">
                    <div className="text-sm text-gray-500 order-2 sm:order-1">
                      {t("adminSettings.admins.showing", {
                        count: filteredAdmins.length,
                        total: admins.length,
                      })}
                    </div>
                    {filteredAdmins.length > 10 && (
  <div className="flex items-center gap-2 order-1 sm:order-2">
    <button
      onClick={handlePreviousPage}
      disabled={currentPage === 1}
      className={`p-2 rounded-lg border ${
        currentPage === 1
          ? "text-gray-300 border-gray-200 cursor-not-allowed"
          : "text-gray-600 border-gray-300 hover:bg-gray-50"
      }`}
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
    <span className="text-sm text-gray-600">
      {t("adminSettings.admins.pagination", {
        current: currentPage,
        total: totalPages,
      })}
    </span>
    <button
      onClick={handleNextPage}
      disabled={currentPage === totalPages}
      className={`p-2 rounded-lg border ${
        currentPage === totalPages
          ? "text-gray-300 border-gray-200 cursor-not-allowed"
          : "text-gray-600 border-gray-300 hover:bg-gray-50"
      }`}
    >
      <ChevronRight className="w-5 h-5" />
    </button>
  </div>
)}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>

        {/* Add Admin Modal */}
        {userRole === "admin" && isAddModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-[19px] p-4 sm:p-6 w-full max-w-[400px] shadow-md">
              <h3 className="text-lg font-semibold text-neutral-700 mb-4">
                {t("adminSettings.admins.add")}
              </h3>
              <form onSubmit={handleAddAdmin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("adminSettings.admins.form.username")}
                  </label>
                  <input
                    type="text"
                    value={newAdmin.username}
                    onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-transparent"
                    required
                    placeholder={t("adminSettings.admins.form.username")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("adminSettings.admins.form.password")}
                  </label>
                  <input
                    type="password"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-transparent"
                    required
                    placeholder={t("adminSettings.admins.form.passwordPlaceholderForAddUser")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("adminSettings.admins.form.role")}
                  </label>
                  <select
                    value={newAdmin.role}
                    onChange={(e) =>
                      setNewAdmin({ ...newAdmin, role: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-transparent"
                  >
                    <option value="admin">
                      {t("adminSettings.admins.form.roleAdmin")}
                    </option>
                    <option value="super user">
                      {t("adminSettings.admins.form.roleSuperUser")}
                    </option>
                  </select>
                </div>
                {newAdmin.role === "super user" && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={newAdmin.isActive}
                      onChange={(e) =>
                        setNewAdmin({ ...newAdmin, isActive: e.target.checked })
                      }
                      className="w-4 h-4 text-zinc-800 border-gray-300 rounded focus:ring-zinc-800"
                    />
                    <label
                      htmlFor="isActive"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      {t("adminSettings.admins.form.active")}
                    </label>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsAddModalOpen(false)}
                    className="w-full sm:w-auto px-4 py-2 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {t("adminSettings.admins.form.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-900 transition-colors"
                  >
                    {t("adminSettings.admins.form.submit")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Admin Modal */}
        {userRole === "admin" && isEditModalOpen && editAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-[19px] p-4 sm:p-6 w-full max-w-[400px] shadow-md">
              <h3 className="text-lg font-semibold text-neutral-700 mb-4">
                {t("adminSettings.admins.edit")}
              </h3>
              <form onSubmit={handleEditAdmin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("adminSettings.admins.form.username")}
                  </label>
                  <input
                    type="text"
                    value={editAdmin.username}
                    onChange={(e) =>
                      setEditAdmin({ ...editAdmin, username: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("adminSettings.admins.form.password")}
                  </label>
                  <input
                    type="password"
                    value={editAdmin.password}
                    onChange={(e) =>
                      setEditAdmin({ ...editAdmin, password: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-transparent"
                    placeholder={t(
                      "adminSettings.admins.form.passwordPlaceholder"
                    )}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("adminSettings.admins.form.role")}
                  </label>
                  <select
                    value={editAdmin.role}
                    onChange={(e) =>
                      setEditAdmin({ ...editAdmin, role: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-transparent"
                  >
                    <option value="admin">
                      {t("adminSettings.admins.form.roleAdmin")}
                    </option>
                    <option value="super user">
                      {t("adminSettings.admins.form.roleSuperUser")}
                    </option>
                  </select>
                </div>
                {editAdmin.role === "super user" && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editIsActive"
                      checked={editAdmin.isActive}
                      onChange={(e) =>
                        setEditAdmin({
                          ...editAdmin,
                          isActive: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-zinc-800 border-gray-300 rounded focus:ring-zinc-800"
                    />
                    <label
                      htmlFor="editIsActive"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      {t("adminSettings.admins.form.active")}
                    </label>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="w-full sm:w-auto px-4 py-2 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {t("adminSettings.admins.form.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-900 transition-colors"
                  >
                    {t("adminSettings.admins.form.save")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;